import React, { useState, useEffect } from 'react';
import {
  ArrowLeft, Check, Star, Package, Loader2, MapPin,
  ChevronDown, ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;

const PRODUCT_CATEGORY_LABELS = {
  meal_plan: 'Meal Plans', meal: 'Individual Meals', grocery_pack: 'Grocery Packs',
  fuel_voucher: 'Fuel Vouchers', ride_voucher: 'Ride Vouchers', transit_pass: 'Transit Passes',
  intercity: 'Intercity Travel', health_plan: 'Health Plans', fitness: 'Fitness & Wellness',
  life_insurance: 'Life Insurance', rent_service: 'Rent Services', savings: 'Savings Plans',
  subscription: 'Subscriptions', course_credit: 'Course Credits', store_credit: 'Store Credits',
  entertainment: 'Entertainment', general: 'Products',
};

export default function PublicVendorDetail({ vendorId, onBack }) {
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null);

  useEffect(() => {
    if (!vendorId) return;
    setLoading(true);
    fetch(`${API_URL}/api/offer-letters/public/vendor/${vendorId}`)
      .then(r => r.json())
      .then(data => { if (!data.detail) setVendor(data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [vendorId]);

  if (loading) return (
    <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#D64D7A]" /></div>
  );

  if (!vendor) return (
    <div className="text-center py-12">
      <Package className="w-8 h-8 text-stone-300 mx-auto mb-2" />
      <p className="text-sm text-[#636E72]">Vendor not found</p>
      <button onClick={onBack} className="text-sm text-[#D64D7A] font-medium mt-2 hover:underline">Go back</button>
    </div>
  );

  const productGroups = vendor.product_groups || {};

  return (
    <div className="space-y-5 animate-in fade-in slide-in-from-right-4 duration-300" data-testid="public-vendor-detail">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="p-2 -ml-2 rounded-lg hover:bg-stone-100 transition-colors" data-testid="vendor-detail-back">
          <ArrowLeft className="w-5 h-5 text-[#636E72]" />
        </button>
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-[#2D3436] font-[Manrope]">{vendor.name}</h2>
          <p className="text-xs text-[#636E72]">{vendor.total_products} products available</p>
        </div>
      </div>

      {/* Description */}
      <div className="bg-white rounded-xl border border-stone-100 p-4">
        <p className="text-sm text-[#636E72]">{vendor.description}</p>
        {vendor.metadata?.locations && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[#636E72]">
            <MapPin className="w-3 h-3" /> <span>{vendor.metadata.locations}</span>
          </div>
        )}
      </div>

      {/* Products */}
      {Object.entries(productGroups).map(([groupKey, products]) => (
        <div key={groupKey} className="space-y-2">
          <h3 className="text-sm font-semibold text-[#2D3436]">{PRODUCT_CATEGORY_LABELS[groupKey] || groupKey}</h3>
          {products.map(p => {
            const isExpanded = expandedProduct === p.id;
            return (
              <div key={p.id} className="bg-white rounded-xl border border-stone-100 overflow-hidden" data-testid={`pub-product-${p.id}`}>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#2D3436]">{p.name}</span>
                        {p.is_popular && (
                          <span className="flex items-center gap-0.5 text-[9px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                            <Star className="w-2.5 h-2.5 fill-amber-500" /> Popular
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-[#636E72] mt-0.5">{p.description}</p>
                    </div>
                    <span className="text-sm font-extrabold text-[#2D3436] font-[Manrope] flex-shrink-0">{fmt(p.price)}</span>
                  </div>
                  {p.contents?.length > 0 && (
                    <button onClick={() => setExpandedProduct(isExpanded ? null : p.id)} className="flex items-center gap-1 mt-2 text-xs text-[#D64D7A] font-medium hover:underline">
                      {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      {isExpanded ? 'Hide' : 'See'} what's included
                    </button>
                  )}
                </div>
                {isExpanded && p.contents && (
                  <div className="bg-stone-50 border-t border-stone-100 px-4 py-3">
                    <div className="grid grid-cols-2 gap-1.5">
                      {p.contents.map((item, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs text-[#2D3436]">
                          <Check className="w-3 h-3 text-emerald-500 flex-shrink-0" /> <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <p className="text-[10px] text-[#636E72] text-center">
        You'll have access to all {vendor.name} products once your benefits are activated.
      </p>
    </div>
  );
}
